"use client";

import { Button, Icon, View } from "@/components";
import { useScrollable } from "@/hooks/useScrollable";
import cn from "@/utils/cn";

import type { JSX, ReactNode } from "react";

export interface HorizontalCarouselProps {
  children: ReactNode;
  className?: string;
  amountToScroll?: number;
}

/**
 * @name HorizontalCarousel
 * @description
 * Componente molecular que renderiza un contenedor con desplazamiento horizontal (carousel).
 * Maneja automáticamente el desbordamiento mostrando flechas de navegación izquierda y derecha cuando el contenido excede el ancho del contenedor.
 * Oculta la barra de desplazamiento nativa para una apariencia más limpia, pero permite el desplazamiento mediante las flechas o gestos táctiles/mouse.
 * Utiliza el hook `useScrollable` para la lógica de scroll y átomos `Button`/`Icon` para la interfaz.
 *
 * @param {HorizontalCarouselProps} props - Propiedades del componente.
 * @param {ReactNode} props.children - El contenido que se desplazará horizontalmente. Puede ser cualquier elemento de React (lista de tarjetas, botones, imágenes, etc.).
 * @param {string} [props.className] - Clases CSS adicionales opcionales para el contenedor raíz. Útil para ajustar márgenes, ancho o posicionamiento.
 * @param {number} [props.amountToScroll=200] - Cantidad de píxeles a desplazar cuando se hace clic en las flechas de navegación. Por defecto es 200px.
 *
 * @example
 * ```tsx
 * // Ejemplo básico con divs de colores
 * <HorizontalCarousel amountToScroll={300}>
 *   <div className="w-40 h-40 bg-red-500 shrink-0">Item 1</div>
 *   <div className="w-40 h-40 bg-blue-500 shrink-0">Item 2</div>
 *   <div className="w-40 h-40 bg-green-500 shrink-0">Item 3</div>
 * </HorizontalCarousel>
 *
 * // Ejemplo con lista de botones (como filtros)
 * <HorizontalCarousel className="w-full">
 *   {categories.map((cat) => (
 *     <Button key={cat.id} action={() => handleSelect(cat.id)}>
 *       {cat.name}
 *     </Button>
 *   ))}
 * </HorizontalCarousel>
 * ```
 *
 * @returns {JSX.Element} El componente de carrusel horizontal renderizado.
 */
const HorizontalCarousel = ({
  children,
  className = "",
  amountToScroll = 200
}: HorizontalCarouselProps): JSX.Element => {
  const { scrollRef, canScrollLeft, canScrollRight, scroll } = useScrollable();

  return (
    <View
      className={cn(
        "relative flex items-center gap-2 w-full max-w-full overflow-hidden",
        className
      )}
    >
      {canScrollLeft && (
        <Button
          className="h-8 w-8 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10"
          aria-label="Previous items"
          action={() => scroll(-amountToScroll)}
        >
          <Icon name="chevron-left:bx:bx" className="text-2xl" />
        </Button>
      )}

      <View
        ref={scrollRef}
        className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide scroll-smooth flex-1 pb-2 select-none cursor-grab"
      >
        {children}
      </View>

      {canScrollRight && (
        <Button
          className="h-8 w-8 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Next items"
          action={() => scroll(amountToScroll)}
        >
          <Icon name="chevron-right:bx:bx" className="text-2xl" />
        </Button>
      )}
    </View>
  );
};

export default HorizontalCarousel;
